import React, { useState, useEffect } from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Button,
  SimpleGrid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Stack,
} from "@chakra-ui/react";
import {
  FaLanguage,
  FaThermometerHalf,
  FaCreditCard,
  FaChild,
} from "react-icons/fa";
import { FaPerson } from "react-icons/fa6";
import {
  MdLocationOn,
  MdOutlineAccessTime,
  MdCurrencyExchange,
  MdKeyboardArrowDown,
} from "react-icons/md";
import axios from "axios";
import countries from "i18n-iso-countries";
import eng from "i18n-iso-countries/langs/en.json";
import { ReactComponent as ExchangeIcon } from "../../assets/icons/exchange.svg";
import moment from "moment-timezone";
import cityTimezones from "city-timezones";
import currencySymbolMap from "currency-symbol-map";
import Translate from "../../components/LanguageTranslation/Translate";

import { Editable, EditableInput, EditablePreview } from "@chakra-ui/react";
import axiosInstance from "../../helpers/axiosInstance";

// Register the English locale for country codes
countries.registerLocale(eng);

const InfoCard = ({ tripId, info, city, country, currency }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [generalInfo, setGeneralInfo] = useState({
    currency_code: "",
    currency_name: "",
    language: "",
  });
  const currencyCode = generalInfo.currency_code || "N/A";
  const currencySymbol = currencySymbolMap(currencyCode) || currencyCode;

  const [localTime, setLocalTime] = useState({
    utc_time: "",
    utc_offset: "",
  });
  const [temperature, setTemperature] = useState("");
  const [currencyRate, setCurrencyRate] = useState("");

  // Convert country name to country code dynamically
  const countryCode = country ? countries.getAlpha2Code(country, "en") : null;

  // Fetch country info and reset states
  useEffect(() => {
    // Reset states when country or city changes
    setGeneralInfo({
      currency_code: "",
      currency_name: "",
      language: "",
    });
    setLocalTime({
      utc_time: "",
      utc_offset: "",
    });
    setTemperature("");
    setCurrencyRate(""); // Reset currency rate on country change

    // Fetch general country info based on the country
    const fetchCountryInfo = async () => {
      try {
        const response = await axios.get(`/general_info/country/${country}`);
        setGeneralInfo(response.data);
      } catch (error) {
        console.error("Error fetching country info:", error);
      }
    };

    if (country) {
      fetchCountryInfo();
    }
  }, [country]);

  // Fetch local time and temperature based on the city
  useEffect(() => {
    // Reset state when city changes
    setLocalTime({
      utc_time: "",
      utc_offset: "",
    });
    setTemperature("");

    const fetchLocalTime = async () => {
      try {
        const lookup = cityTimezones.lookupViaCity(city);
        // console.log(lookup)
        const cityWithMaxPop = lookup.reduce((maxCity, currentCity) => {
          return currentCity.pop > maxCity.pop ? currentCity : maxCity;
        });
        const currentTime = moment().tz(cityWithMaxPop.timezone);
        const formattedTime = currentTime.format("HH:mm"); // Get the formatted time
        const utcOffset = currentTime.utcOffset(); // Get the UTC offset in minutes (e.g., -180 for UTC+03:00)

        // Update the localTime state with both time and UTC offset
        setLocalTime({
          utc_time: formattedTime,
          utc_offset: `UTC${utcOffset >= 0 ? "+" : ""}${utcOffset / 60}`, // Convert to hours (e.g., UTC+3, UTC-2)
        });
        // console.log("fetchLocalTime",response.data);
      } catch (error) {
        console.error("Error fetching local time:", error);
      }
    };

    const fetchTemperature = async () => {
      try {
        const response = await axios.get(`/general_info/temperature/${city}`);
        setTemperature(response.data.temperature);
      } catch (error) {
        console.error("Error fetching temperature:", error);
      }
    };

    if (city) {
      fetchLocalTime();
      fetchTemperature();
    }
  }, [city]); // The `city` dependency ensures that the APIs are refetched when `city` changes

  // Fetch currency rate based on currency code after it is set in generalInfo
  useEffect(() => {
    const fetchCurrencyRate = async () => {
      if (generalInfo.currency_code) {
        try {
          const response = await axios.get(
            `/general_info/currency_rate/${currency}/${generalInfo.currency_code}`
          );
          setCurrencyRate(response.data.conversion_rate);
        } catch (error) {
          console.error("Error fetching currency rate:", error);
        }
      }
    };

    if (generalInfo.currency_code) {
      fetchCurrencyRate(); // Fetch currency rate only after currency_code is available
    }
  }, [generalInfo.currency_code, currency]); // Fetch the rate when currency_code is set

  const limitDigits = (value) => {
    value = value.replace(/\D/g, ""); // Remove non-numeric characters
    if (value.length > 3) value = value.slice(0, 3); // Limit to 3 digits
    return value;
  };

  const changeAdults = (e) => {
    e.target.value = limitDigits(e.target.value);
    // The value will be handled by the Editable component's onSubmit
  };

  const changeChildren = (e) => {
    e.target.value = limitDigits(e.target.value);
    // The value will be handled by the Editable component's onSubmit
  };

  const updateTripTravelers = async () => {
    try {
      await axiosInstance.put(`/bucket-list/${tripId}/update/travelers`, {
        number_of_adults: info?.travelers?.adults,
        number_of_children: info?.travelers?.children,
      });
    } catch (error) {
      console.error("Error updating trip completion:", error);
    }
  };

  const services = [
    {
      name: "GetTransfer",
      description: "Affordable airport transfers & private rides",
      action: "Find a Ride",
      link: "https://gettransfer.tpk.ro/5atEJ4ob",
    },
    {
      name: "Radical Storage",
      description: "Hassle-free luggage storage in 500+ cities",
      action: "Book Storage",
      link: "https://radicalstorage.tpk.ro/rXgJqnwt",
    },
    {
      name: "Nomad Insurance",
      description: "Affordable travel medical cover",
      action: "Get insured",
      link: "https://safetywing.com/?referenceID=26306017&utm_source=26306017&utm_medium=Ambassador",
    },
    {
      name: "Wise",
      description: "Save on exchange fees",
      action: "Learn more",
      link: "https://wise.prf.hn/l/NdBGMYP",
    },

    {
      name: "NordVPN",
      description: "Browse safely anywhere",
      action: "Stay safe",
      link: "https://go.nordvpn.net/aff_c?offer_id=15&aff_id=127435",
    },
    {
      name: "Sailly",
      description: "Wi-Fi for digital nomads",
      action: "Discover",
      link: "https://go.saily.site/aff_c?offer_id=101&aff_id=9950",
    },
  ];

  return (
    <Box
      bg="white"
      p={{ base: 2, md: 4 }}
      borderRadius="md"
      boxShadow="md"
      border="1px solid"
      borderColor="gray.200"
      mb={{ base: 2, md: 4 }}
      mt={{ base: 2, md: 4 }}
    >
      <Box>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 4, md: 1 }}>
          {/* Left Column */}
          <VStack w="auto" align="start" spacing={4}>
            <HStack>
              <Text size={16} fontWeight="bold" mb={0}>
                {currencySymbol}
              </Text>
              <Text
                mb={0}
                ml={2}
                fontWeight="semibold"
                fontSize={{ base: "xs", md: "sm" }}
              >
                {currencyCode}
              </Text>
            </HStack>
            <HStack alignItems="flex-start" justifyContent="flex-start">
              <VStack spacing={0} alignItems="flex-start">
                <HStack>
                  <FaCreditCard size={16} />
                  <Text
                    mb={0}
                    ml={2}
                    fontWeight="semibold"
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {<Translate text={generalInfo.currency_name} /> || "N/A"}
                  </Text>
                </HStack>
                {/* <Link
                  href="https://wise.prf.hn/l/NdBGMYP" // URL to be opened
                  fontSize={{ base: "xs", md: "xs" }} // Responsive font size
                  mb={0} // Margin bottom
                  isExternal // Opens the link in a new tab
                  color="#286328" // Link color
                  _hover={{
                    textDecoration: "underline", // Underline on hover
                  }}
                >
                  https://wise.prf.hn/l/NdBGMYP
                </Link> */}
              </VStack>
            </HStack>
            <HStack>
              <MdOutlineAccessTime size={16} />
              <Text
                mb={0}
                ml={2}
                fontWeight="semibold"
                fontSize={{ base: "xs", md: "sm" }}
              >
                {localTime.utc_time} {localTime.utc_offset || "N/A"}
              </Text>
            </HStack>
            <HStack>
              <FaLanguage size={25} />
              <Box bg="gray.100" borderRadius="sm" p={1}>
                <Text
                  mb={0}
                  fontWeight="semibold"
                  fontSize={{ base: "xs", md: "sm" }}
                >
                  {<Translate text={generalInfo.language} /> || "N/A"}
                </Text>
              </Box>
            </HStack>
          </VStack>

          {/* Right Column */}
          <VStack w="auto" align="start" spacing={4} mt={{ base: 4, md: 0 }}>
            <HStack>
              <MdCurrencyExchange size={16} />
              <Text
                mb={0}
                ml={2}
                fontWeight="semibold"
                fontSize={{ base: "xs", md: "sm" }}
              >
                {currencyRate || "N/A"}
              </Text>
            </HStack>
            <HStack>
              <MdLocationOn size={16} />
              <Text
                mb={0}
                ml={2}
                fontWeight="semibold"
                fontSize={{ base: "xs", md: "sm" }}
              >
                <Translate text={city} />
              </Text>
            </HStack>
            <HStack>
              <FaThermometerHalf size={16} />
              <Text
                mb={0}
                ml={2}
                fontWeight="semibold"
                fontSize={{ base: "xs", md: "sm" }}
              >
                {temperature
                  ? `${Math.round(parseFloat(temperature))}°C`
                  : "N/A"}
              </Text>
            </HStack>
            <HStack alignItems="center" spacing="2">
              {/* Adults Section */}
              <HStack spacing="1">
                <Editable
                  value={info?.travelers?.adults || 0}
                  placeholder={0}
                  onSubmit={updateTripTravelers}
                >
                  <EditablePreview
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                    px={2}
                    _hover={{ borderColor: "blue.500", cursor: "pointer" }}
                  />
                  <EditableInput
                    maxWidth="32px"
                    type="number"
                    onChange={changeAdults}
                  />
                </Editable>
                <FaPerson size={16} />
                <Text fontSize={{ base: "xs", md: "sm" }} mb={0}>
                  <Translate text="Adults" />
                </Text>
              </HStack>

              {/* Children Section */}
              <HStack spacing="1">
                <Editable
                  value={info?.travelers?.children || 0}
                  placeholder={0}
                  onSubmit={updateTripTravelers}
                >
                  <EditablePreview
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                    px={2}
                    _hover={{ borderColor: "blue.500", cursor: "pointer" }}
                  />
                  <EditableInput
                    maxWidth="32px"
                    type="number"
                    onChange={changeChildren}
                  />
                </Editable>
                <FaChild size={16} />
                <Text fontSize={{ base: "xs", md: "sm" }} mb={0}>
                  <Translate text="Children" />
                </Text>
              </HStack>
            </HStack>
          </VStack>
        </SimpleGrid>
      </Box>
      <Popover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="bottom-start"
        closeOnBlur={true}
        closeOnEsc={true}
        autoFocus={false}
      >
        <PopoverTrigger>
          <Button
            leftIcon={
              <Box as="span" fontSize="16px" color="blue.500">
                🌍
              </Box>
            }
            rightIcon={<MdKeyboardArrowDown size={16} />}
            onClick={() => setIsOpen(!isOpen)}
            mt={4}
            bg="white"
            color="black"
            border="1px solid"
            borderColor="gray.300"
            size={{ base: "sm", md: "sm" }}
            _hover={{ bg: "gray.50" }}
          >
            Explore Smarter
          </Button>
        </PopoverTrigger>
        <PopoverContent
          p={4}
          border="1px solid"
          borderColor="black"
          borderRadius="md"
          w="400px"
          focusBorderColor="transparent"
        >
          <PopoverBody>
            <Stack spacing={2} w="100%">
              {services.map((service, index) => (
                <Box
                  key={index}
                  as="a"
                  href={service.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  bg="gray.50" // card background
                  p={3}
                  borderRadius="md"
                  cursor="pointer"
                  _hover={{ bg: "gray.100" }}
                  textDecoration="none"
                  color="inherit"
                >
                  <SimpleGrid columns={2} spacing={2}>
                    {/* left cell: service name */}
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      cursor="pointer"
                      _hover={{ color: "blue.500" }}
                      // onClick={() => window.open(service.link, "_blank")}
                      mb={0}
                    >
                      {service.name}
                    </Text>

                    {/* right cell: description + arrow */}
                    <HStack spacing={1}>
                      <Text fontSize="xs" color="gray.600" mb={0}>
                        {service.description}
                      </Text>
                      <Text
                        fontSize="xs"
                        cursor="pointer"
                        mb={0}
                      >
                        →
                      </Text>
                    </HStack>
                  </SimpleGrid>
                </Box>
              ))}
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default InfoCard;
